/**
 * GS://PROTOCOLO
 * Arquivo: src/pages/EditPurchasePage.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2 – Layout padronizado dos itens, campos estreitos p/ SKU e Cód. Fornecedor, rótulos sem quebra.
 * Impacto da Versão: [src/pages/EditPurchasePage.jsx]
 * Data: 30/10/2025
 * Responsável: GPT-5 Thinking
 *
 * Mudanças desta versão:
 * - ALINHADO: grid dos itens em 12 colunas sem espaços sobrando.
 * - AJUSTADO: SKU e Cód. Fornecedor com largura curta; rótulos com whitespace-nowrap.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import {
  getPurchaseById,
  updatePurchase,
  getSuppliers,
  getCategories,
} from '../services/firestoreService';
import { Loader2, Plus, X, ArrowLeft, Save } from 'lucide-react';

/* ====================== Utilitários ====================== */
const currencyToNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  // aceita "1596" => 15.96 e "15,96" => 15.96
  const onlyDigits = String(v).replace(/\D/g, '');
  if (!onlyDigits) return 0;
  const asCents = parseInt(onlyDigits, 10);
  return (asCents / 100);
};

const numberToBRL = (n) =>
  (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const tsToInputDate = (ts) => {
  if (!ts) return new Date().toISOString().slice(0, 10);
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return new Date(+d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/* ====================== Página ====================== */
export default function EditPurchasePage({ openModal }) {
  const { purchaseId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);

  // Cabeçalho da compra
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierFriendlyCode, setSupplierFriendlyCode] = useState('');

  // Itens
  const [items, setItems] = useState([
    {
      productName: '',
      supplierId: '',
      supplierProductCode: '',
      categoryId: '',
      quantity: 1,
      unitCost: 0,
      discount: 0,
      productId: null,
      internalSku: '', // SKU GS
      isNewProduct: false,
    },
  ]);

  /* ====================== Load inicial ====================== */
  const fetchAll = useCallback(async () => {
    if (!purchaseId) {
      openModal(<p className="text-red-500">ID da compra ausente na URL.</p>, 'Erro');
      navigate('/purchases');
      return;
    }
    setLoading(true);
    try {
      const [suppliersData, categoriesData, purchase] = await Promise.all([
        getSuppliers(),
        getCategories(),
        getPurchaseById(purchaseId),
      ]);

      setSuppliers(suppliersData || []);
      setCategories(categoriesData || []);

      if (!purchase) {
        openModal(<p className="text-red-500">Compra não encontrada.</p>, 'Erro');
        navigate('/purchases');
        return;
      }

      setPurchaseDate(tsToInputDate(purchase.purchaseDate));
      setInvoiceNumber(purchase.invoiceNumber || '');

      setSupplierId(purchase.supplierId || '');
      setSupplierName(purchase.supplierName || '');
      setSupplierFriendlyCode(purchase.supplierFriendlyCode || '');

      const normalizedItems = Array.isArray(purchase.items)
        ? purchase.items.map((it) => ({
            productName: it.productName || '',
            supplierId: it.supplierId || purchase.supplierId || '',
            supplierProductCode: it.supplierProductCode || '',
            categoryId: it.categoryId || '',
            quantity: Number(it.quantity) || 0,
            unitCost: Number(it.unitCost) || 0,
            discount: Number(it.discount) || 0,
            productId: it.productId || null,
            internalSku: it.internalSku || '',
            isNewProduct: !!it.isNewProduct,
          }))
        : [];

      setItems(
        normalizedItems.length
          ? normalizedItems
          : [
              {
                productName: '',
                supplierId: purchase.supplierId || '',
                supplierProductCode: '',
                categoryId: '',
                quantity: 1,
                unitCost: 0,
                discount: 0,
                productId: null,
                internalSku: '',
                isNewProduct: false,
              },
            ],
      );
    } catch (err) {
      console.error('[EditPurchasePage] load error:', err);
      openModal(
        <p className="text-red-500">Não foi possível carregar a compra. Detalhes no console (F12).</p>,
        'Erro ao carregar',
      );
      navigate('/purchases');
    } finally {
      setLoading(false);
    }
  }, [purchaseId, openModal, navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ====================== Totais ====================== */
  const { subtotal, totalDiscount, grandTotal } = useMemo(() => {
    const sub = items.reduce(
      (acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unitCost) || 0),
      0,
    );
    const disc = items.reduce((acc, it) => acc + (Number(it.discount) || 0), 0);
    return {
      subtotal: sub,
      totalDiscount: disc,
      grandTotal: Math.max(sub - disc, 0),
    };
  }, [items]);

  /* ====================== Handlers ====================== */
  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const arr = [...prev];
      let v = value;
      if (['quantity', 'unitCost', 'discount'].includes(field)) v = currencyToNumber(v);
      arr[index] = { ...arr[index], [field]: v };

      // sincroniza fornecedor principal ao alterar o primeiro item
      if (index === 0 && field === 'supplierId') {
        const sel = suppliers.find((s) => s.id === v);
        setSupplierId(v || '');
        setSupplierName(sel?.name || '');
        setSupplierFriendlyCode(sel?.friendlyCode || '');
      }
      return arr;
    });
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        productName: '',
        supplierId: supplierId || '',
        supplierProductCode: '',
        categoryId: '',
        quantity: 1,
        unitCost: 0,
        discount: 0,
        productId: null,
        internalSku: '',
        isNewProduct: false,
      },
    ]);
  };

  const removeItem = (idx) => {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      const arr = [...prev];
      arr.splice(idx, 1);
      return arr;
    });
  };

  const handleSave = async () => {
    if (!invoiceNumber.trim()) {
      openModal(<p className="text-red-500">Informe o Número da Nota Fiscal.</p>, 'Campo obrigatório');
      return;
    }
    if (items.some((it) => !it.supplierId || !it.categoryId || (Number(it.quantity) || 0) <= 0)) {
      openModal(
        <p className="text-red-500">Fornecedor, categoria e quantidade são obrigatórios em todos os itens.</p>,
        'Campos obrigatórios',
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        purchaseDate: Timestamp.fromDate(new Date(purchaseDate)),
        invoiceNumber: invoiceNumber.trim(),
        supplierId: supplierId || (items[0]?.supplierId || ''),
        supplierName: supplierName || suppliers.find((s) => s.id === supplierId)?.name || '',
        supplierFriendlyCode:
          supplierFriendlyCode || suppliers.find((s) => s.id === supplierId)?.friendlyCode || '',
        items: items.map((it) => ({
          productName: it.productName || '',
          supplierId: it.supplierId || supplierId || '',
          supplierProductCode: it.supplierProductCode || '',
          categoryId: it.categoryId || '',
          quantity: Number(it.quantity) || 0,
          unitCost: Number(it.unitCost) || 0,
          discount: Number(it.discount) || 0,
          productId: it.productId || null,
          internalSku: it.internalSku || '',
          isNewProduct: !!it.isNewProduct,
        })),
        subtotal,
        totalExtraCosts: 0,
        totalPurchaseValue: grandTotal,
        status: 'Concluída',
      };

      await updatePurchase(purchaseId, payload);

      openModal(<p className="text-green-500">Compra atualizada com sucesso.</p>, 'Edição concluída');
      navigate('/purchases');
    } catch (err) {
      console.error('[EditPurchasePage] save error:', err);
      openModal(
        <p className="text-red-500">Ocorreu um erro ao salvar a compra. Verifique o console (F12).</p>,
        'Erro ao salvar',
      );
    } finally {
      setSaving(false);
    }
  };

  /* ====================== Render ====================== */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-black text-white">
        <Loader2 className="animate-spin h-10 w-10 text-brand-gold" />
        <span className="ml-4 text-xl">Carregando compra...</span>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-brand-black text-white font-sans min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-serif">Editar Compra</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/purchases')}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md flex items-center"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-brand-gold text-brand-black font-semibold rounded-md hover:bg-yellow-500 disabled:bg-gray-500 flex items-center"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
            Salvar Alterações
          </button>
        </div>
      </div>

      {/* Detalhes da compra */}
      <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">Detalhes da Compra</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 whitespace-nowrap">Data da Compra</label>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className="mt-1 block w-full bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 whitespace-nowrap">Número da Nota Fiscal</label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Ex: 123456789 ou ABC-XYZ"
              className="mt-1 block w-full bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 whitespace-nowrap">Fornecedor Principal</label>
            <select
              value={supplierId}
              onChange={(e) => {
                const sel = suppliers.find((s) => s.id === e.target.value);
                setSupplierId(e.target.value);
                setSupplierName(sel?.name || '');
                setSupplierFriendlyCode(sel?.friendlyCode || '');
                setItems((prev) => {
                  if (!prev.length) return prev;
                  const arr = [...prev];
                  arr[0] = { ...arr[0], supplierId: e.target.value };
                  return arr;
                });
              }}
              className="mt-1 block w-full bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
            >
              <option value="">Selecione...</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Itens */}
      <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">Itens da Compra</h2>

        {items.map((it, idx) => {
          const itemTotal =
            (Number(it.quantity) || 0) * (Number(it.unitCost) || 0) - (Number(it.discount) || 0);

        /* ------------- LINHA PADRÃO: 12 colunas ------------- */
          return (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 mb-4 p-4 border border-gray-700 rounded-lg relative"
            >
              {items.length > 1 && (
                <button
                  onClick={() => removeItem(idx)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-400"
                  title="Remover Item"
                >
                  <X size={18} />
                </button>
              )}

              {/* SKU (GS) – curto */}
              <div className="col-span-12 md:col-span-2">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">SKU (GS)</label>
                <input
                  value={it.internalSku || ''}
                  disabled
                  className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white max-w-[160px]"
                />
              </div>

              {/* Código do Produto do Fornecedor – curto */}
              <div className="col-span-12 md:col-span-2">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">
                  Cód. Prod. Fornecedor
                </label>
                <input
                  value={it.supplierProductCode || ''}
                  onChange={(e) => handleItemChange(idx, 'supplierProductCode', e.target.value)}
                  className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white max-w-[180px]"
                />
              </div>

              {/* Nome do Produto – longo */}
              <div className="col-span-12 md:col-span-3">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">Nome do Produto</label>
                <input
                  value={it.productName}
                  onChange={(e) => handleItemChange(idx, 'productName', e.target.value)}
                  className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                  placeholder="Ex: Colar Gravata..."
                />
              </div>

              {/* Categoria – médio */}
              <div className="col-span-12 md:col-span-2">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">Categoria</label>
                <select
                  value={it.categoryId}
                  onChange={(e) => handleItemChange(idx, 'categoryId', e.target.value)}
                  className="form-select w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                >
                  <option value="">Selecione...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Qtde – curto */}
              <div className="col-span-6 md:col-span-1">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">Qtde.</label>
                <input
                  type="number"
                  min="0"
                  value={it.quantity}
                  onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                  className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                />
              </div>

              {/* Custo Unitário – curto com máscara por centavos implícitos */}
              <div className="col-span-6 md:col-span-1">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">Custo Unitário (R$)</label>
                <input
                  inputMode="numeric"
                  value={
                    String(Math.round((Number(it.unitCost) || 0) * 100)).padStart(3, '0').replace(
                      /(\d+)(\d{2})$/,
                      '$1,$2',
                    )
                  }
                  onChange={(e) => handleItemChange(idx, 'unitCost', e.target.value)}
                  className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                />
              </div>

              {/* Desconto – curto */}
              <div className="col-span-6 md:col-span-1">
                <label className="text-sm font-medium text-gray-400 whitespace-nowrap">Desconto (R$)</label>
                <input
                  inputMode="numeric"
                  value={
                    String(Math.round((Number(it.discount) || 0) * 100)).padStart(3, '0').replace(
                      /(\d+)(\d{2})$/,
                      '$1,$2',
                    )
                  }
                  onChange={(e) => handleItemChange(idx, 'discount', e.target.value)}
                  className="w-full mt-1 bg-gray-800 p-2 rounded-md border-gray-600 text-white"
                />
              </div>

              {/* Total do item – linha fina no rodapé do card */}
              <div className="col-span-12 flex justify-end items-end">
                <div className="text-right text-sm">
                  <span className="text-gray-400 mr-2">Total do Item</span>
                  <span className="font-semibold text-brand-gold">{numberToBRL(itemTotal)}</span>
                </div>
              </div>
            </div>
          );
        })}

        <button
          onClick={addItem}
          className="flex items-center text-brand-gold hover:text-yellow-500 font-semibold mt-2"
        >
          <Plus size={18} className="mr-2" /> Adicionar Item
        </button>
      </div>

      {/* Resumo */}
      <div className="bg-gray-800 p-4 rounded-lg flex justify-end items-center space-x-8">
        <div className="text-right">
          <p className="text-gray-400">
            Subtotal dos Itens: <span className="font-semibold text-white">{numberToBRL(subtotal)}</span>
          </p>
          <p className="text-gray-400">
            Descontos: <span className="font-semibold text-red-400">-{numberToBRL(totalDiscount)}</span>
          </p>
          <p className="text-xl font-bold text-brand-gold">Total da Compra: {numberToBRL(grandTotal)}</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand-gold text-brand-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-500 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
          style={{ minWidth: '160px' }}
        >
          {saving ? <Loader2 className="animate-spin" /> : 'Salvar Alterações'}
        </button>
      </div>
    </div>
  );
}
